import { Component, ElementRef, HostListener, computed, effect, inject, signal, viewChild } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { APP_VERSION } from '../../../core/config/app-version';
import { ConfigService } from '../../../core/config/config.service';
import { ThemeService } from '../../../core/theme/theme.service';
import { AuthService } from '../../../services/auth.service';
import { BuscaGlobalComponent } from '../../../shared/busca-global.component/busca-global.component';
import { BuscaGlobalService } from '../../../services/busca-global.service';
import { PainelResumoStore } from '../../../services/painel-resumo.store';

interface ItemMenu {
  id: string;
  rota: string;
  rotulo: string;
  icone: string;
  badge: number | null;
}

interface GrupoMenu {
  id: string;
  rotulo: string;
  itens: ItemMenu[];
}

@Component({
  selector: 'app-layout',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, BuscaGlobalComponent],
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.css'],
})
export class LayoutComponent {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly config = inject(ConfigService);
  private readonly store = inject(PainelResumoStore);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly tema = inject(ThemeService);
  readonly busca = inject(BuscaGlobalService);

  private readonly botaoMenu = viewChild<ElementRef<HTMLButtonElement>>('botaoMenu');
  private readonly botaoUsuario = viewChild<ElementRef<HTMLButtonElement>>('botaoUsuario');
  private readonly painelUsuario = viewChild<ElementRef<HTMLElement>>('painelUsuario');
  private readonly sidebar = viewChild<ElementRef<HTMLElement>>('sidebar');

  readonly versao = APP_VERSION;
  readonly usuario = this.auth.usuario;
  readonly papel = this.auth.papel;
  readonly isAdmin = this.auth.isAdmin;
  readonly tenantsHabilitado = computed(() => this.config.settings()?.features?.tenants === true);

  readonly sidebarAberta = signal(false);
  readonly menuUsuarioAberto = signal(false);

  readonly rotuloTema = computed(() => `Tema: ${this.tema.preferencia()}`);
  readonly temaEscuro = computed(() => this.tema.efetivo() === 'escuro');

  readonly grupos = computed<GrupoMenu[]>(() => {
    const r = this.store.resumo();
    const clientes = r && r.totalClientes > 0 ? r.totalClientes : null;
    const tenantsTotal = r?.tenants?.totalHabilitados;
    const tenants = tenantsTotal && tenantsTotal > 0 ? tenantsTotal : null;
    const st = r?.tenants?.ultimaExecucao?.status;
    const migracoes = st === 'EXECUTANDO' || st === 'PAUSADA' ? 1 : null;
    const flag = this.tenantsHabilitado();
    const admin = this.isAdmin();

    const todos: GrupoMenu[] = [
      {
        id: 'geral',
        rotulo: 'Visão geral',
        itens: [{ id: 'painel', rota: '/home', rotulo: 'Visão geral', icone: 'bi-speedometer2', badge: null }],
      },
      {
        id: 'clientes',
        rotulo: 'Clientes',
        itens: [
          { id: 'clientes', rota: '/clientes', rotulo: 'Clientes', icone: 'bi-people', badge: clientes },
          ...(flag ? [{ id: 'tenants', rota: '/tenants', rotulo: 'Tenants', icone: 'bi-diagram-3', badge: tenants }] : []),
        ],
      },
      {
        id: 'plataforma',
        rotulo: 'Plataforma',
        itens: [
          { id: 'servidores', rota: '/servidores', rotulo: 'Servidores', icone: 'bi-hdd-network', badge: null },
          { id: 'monitoramento', rota: '/monitoramento', rotulo: 'Monitoramento', icone: 'bi-activity', badge: null },
          { id: 'versoes', rota: '/versoes', rotulo: 'Versões', icone: 'bi-tag', badge: null },
          ...(flag
            ? [{ id: 'migracoes', rota: '/migracoes', rotulo: 'Migrações', icone: 'bi-arrow-repeat', badge: migracoes }]
            : []),
        ],
      },
      {
        id: 'seguranca',
        rotulo: 'Segurança',
        itens: [
          { id: 'logins', rota: '/logins', rotulo: 'Logins do ERP', icone: 'bi-box-arrow-in-right', badge: null },
          ...(admin
            ? [
                { id: 'auditoria', rota: '/auditoria', rotulo: 'Auditoria', icone: 'bi-journal-text', badge: null },
                { id: 'usuarios', rota: '/usuarios', rotulo: 'Usuários', icone: 'bi-person-gear', badge: null },
              ]
            : []),
        ],
      },
    ];
    return todos.filter((g) => g.itens.length > 0);
  });

  /** Itens do menu (já filtrados por papel/flag) como atalhos da busca global. */
  readonly atalhos = computed(() =>
    this.grupos().flatMap((g) => g.itens.map((i) => ({ rota: i.rota, rotulo: i.rotulo, icone: i.icone }))),
  );

  constructor() {
    // Falha ao buscar o resumo = sem badges, sem erro na tela.
    this.store.atualizar().subscribe({ error: () => undefined });
    effect(() => {
      if (this.menuUsuarioAberto()) {
        queueMicrotask(() => this.itensMenuUsuario()[0]?.focus());
      }
    });
  }

  /** Skip link: com <base href="/"> o href "#conteudo" recarregaria "/"; foca o <main> sem navegar. */
  irParaConteudo(ev: Event): void {
    ev.preventDefault();
    const main = this.host.nativeElement.querySelector<HTMLElement>('#conteudo');
    main?.focus();
    main?.scrollIntoView?.({ block: 'start' });
  }

  alternarSidebar(): void {
    this.sidebarAberta.update((v) => !v);
    if (this.sidebarAberta()) {
      queueMicrotask(() => this.sidebar()?.nativeElement.querySelector<HTMLElement>('a')?.focus());
    }
  }

  fecharSidebar(devolverFoco = false): void {
    if (!this.sidebarAberta()) {
      return;
    }
    this.sidebarAberta.set(false);
    if (devolverFoco) {
      this.botaoMenu()?.nativeElement.focus();
    }
  }

  alternarMenuUsuario(): void {
    this.menuUsuarioAberto.update((v) => !v);
  }

  fecharMenuUsuario(devolverFoco = false): void {
    if (!this.menuUsuarioAberto()) {
      return;
    }
    this.menuUsuarioAberto.set(false);
    if (devolverFoco) {
      this.botaoUsuario()?.nativeElement.focus();
    }
  }

  aoTeclarBotaoUsuario(ev: KeyboardEvent): void {
    if (ev.key === 'ArrowDown' && !this.menuUsuarioAberto()) {
      ev.preventDefault();
      this.menuUsuarioAberto.set(true);
    }
  }

  aoTeclarMenuUsuario(ev: KeyboardEvent): void {
    const itens = this.itensMenuUsuario();
    const i = itens.indexOf(document.activeElement as HTMLElement);
    let alvo = -1;
    switch (ev.key) {
      case 'ArrowDown':
        alvo = (i + 1) % itens.length;
        break;
      case 'ArrowUp':
        alvo = (i - 1 + itens.length) % itens.length;
        break;
      case 'Home':
        alvo = 0;
        break;
      case 'End':
        alvo = itens.length - 1;
        break;
      case 'Tab':
        this.fecharMenuUsuario();
        return;
      default:
        return;
    }
    ev.preventDefault();
    itens[alvo]?.focus();
  }

  @HostListener('document:keydown.escape')
  aoEscape(): void {
    this.fecharMenuUsuario(true);
    this.fecharSidebar(true);
  }

  @HostListener('document:click', ['$event'])
  aoClicarFora(ev: Event): void {
    if (!this.menuUsuarioAberto()) {
      return;
    }
    const alvo = ev.target as Node;
    const dentro =
      this.painelUsuario()?.nativeElement.contains(alvo) || this.botaoUsuario()?.nativeElement.contains(alvo);
    if (!dentro) {
      this.fecharMenuUsuario();
    }
  }

  sair(): void {
    this.fecharMenuUsuario();
    this.auth.logout();
    void this.router.navigate(['/login']);
  }

  private itensMenuUsuario(): HTMLElement[] {
    const painel = this.painelUsuario()?.nativeElement ?? this.host.nativeElement;
    return Array.from(painel.querySelectorAll<HTMLElement>('[role="menuitem"]'));
  }
}
